import { FC } from 'react'
import {
  Datagrid,
  DateField,
  FilterList,
  FilterListItem,
  FunctionField,
  List,
  TextField,
  TopToolbar,
  useTranslate,
} from 'react-admin'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { useNavigate } from 'react-router-dom'
import { FilterAside } from '../../components'
import { WorkflowExecutionOut } from '../../types/apiRequests'
import { getPerPage } from '../../utils/usePerPage'
import { STATUS_COLORS } from './workflowExecutionColors'

const ExecutionFilters: FC = () => (
  <FilterAside>
    <FilterList label="workflow.execution.filterStatus" icon={<PlayArrowIcon />}>
      <FilterListItem label="workflow.execution.completed" value={{ status: 'completed' }} />
      <FilterListItem label="workflow.execution.running" value={{ status: 'running' }} />
      <FilterListItem label="workflow.execution.failed" value={{ status: 'failed' }} />
      <FilterListItem label="workflow.execution.pending" value={{ status: 'pending' }} />
    </FilterList>
  </FilterAside>
)

const ExecutionListActions: FC = () => {
  const navigate = useNavigate()
  const translate = useTranslate()
  return (
    <TopToolbar>
      <Button
        size="small"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/workflow')}
        sx={{ textTransform: 'none' }}
      >
        {translate('menu.workflows')}
      </Button>
    </TopToolbar>
  )
}

const WorkflowExecutionList: FC = () => {
  const translate = useTranslate()
  return (
    <List
      exporter={false}
      perPage={getPerPage()}
      sort={{ field: 'created', order: 'DESC' }}
      actions={<ExecutionListActions />}
      aside={<ExecutionFilters />}
      title="workflow.actions.executions"
    >
      <Datagrid bulkActionButtons={false} rowClick="show">
        <TextField source="workflow_display_name" label="workflow.execution.workflow" />
        <TextField source="organization_display_name" label="workflow.execution.organization" />
        <FunctionField<WorkflowExecutionOut>
          source="status"
          label="workflow.execution.status"
          render={(record) => (
            <Chip
              icon={
                record.status === 'completed' ? (
                  <CheckCircleIcon sx={{ fontSize: 14 }} />
                ) : record.status === 'failed' ? (
                  <ErrorIcon sx={{ fontSize: 14 }} />
                ) : undefined
              }
              label={translate(`workflow.execution.${record.status}`)}
              size="small"
              color={STATUS_COLORS[record.status] ?? 'default'}
              variant="outlined"
              sx={{ height: 24, fontSize: 11, textTransform: 'capitalize' }}
            />
          )}
        />
        <FunctionField<WorkflowExecutionOut>
          source="trigger_type"
          label="workflow.execution.trigger"
          render={(record) => {
            const triggerStep = record.steps?.find((s) => s.node_type === 'trigger')
            const label = triggerStep?.node_label || triggerStep?.node_id
            return (
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                <Typography component="span" sx={{ fontSize: 13 }}>
                  {record.trigger_type}
                </Typography>
                {label && (
                  <Typography component="span" sx={{ fontSize: 13, color: 'rgba(0,0,0,0.45)' }}>
                    {label}
                  </Typography>
                )}
              </Box>
            )
          }}
        />
        <TextField source="triggered_by" label="workflow.execution.triggeredBy" />
        <DateField source="created" showTime label="workflow.execution.started" />
        <DateField source="finished_at" showTime label="workflow.execution.finished" />
      </Datagrid>
    </List>
  )
}

export default WorkflowExecutionList
